// bridge-contract/contract.ts
//
// Single source of truth for the WebView bridge protocol between
// white-gi-mobile (native) and white-gi (web). Both apps generate a
// copy of this file into their own src/bridge/contract.ts via the
// `generate-bridge-contract` script. Do not import directly from
// outside this directory — Metro and Vite resolve modules
// independently per-workspace.

/**
 * Bump when the bridge protocol changes in a way that breaks
 * compatibility between native and web. Native checks the value web
 * announces in `bridgeReady`; mismatch surfaces SeriesGraphError.
 */
export const BRIDGE_PROTOCOL_VERSION = 1 as const

/**
 * Web → native postMessage type discriminators.
 *
 * NOTE: `nodePositionUpdated` is intentionally IGNORED_BY_MOBILE.
 * The web side persists positions itself via useUpdateSeriesPositions;
 * native has no consumer for the message. Kept in the contract so the
 * web side can keep posting it (existing behavior) and so a future
 * native consumer can be added without a protocol bump.
 */
export const WEB_TO_NATIVE_MESSAGE_TYPES = {
  showMoveInfo: 'showMoveInfo',
  connectionCreated: 'connectionCreated',
  connectionDeleted: 'connectionDeleted',
  nodePositionUpdated: 'nodePositionUpdated', // IGNORED_BY_MOBILE
  authError: 'authError',
  requestDeleteConnection: 'requestDeleteConnection',
  bridgeReady: 'bridgeReady',
  graphRendered: 'graphRendered',
} as const

export type WebToNativeMessageType =
  (typeof WEB_TO_NATIVE_MESSAGE_TYPES)[keyof typeof WEB_TO_NATIVE_MESSAGE_TYPES]

/* Message interfaces. Each carries an index signature so type predicates
 * targeting them can narrow from `{ [k: string]: unknown }` (TS2677). */

export interface ShowMoveInfoMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.showMoveInfo
  seriesMoveId: string
  moveId: string
  name: string
  description: string
  thumbnailUrl?: string | null
  url?: string | null
  [k: string]: unknown
}

export interface ConnectionCreatedMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.connectionCreated
  seriesId: string
  [k: string]: unknown
}

export interface ConnectionDeletedMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.connectionDeleted
  seriesId: string
  [k: string]: unknown
}

export interface NodePositionUpdatedMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.nodePositionUpdated
  seriesId: string
  [k: string]: unknown
}

export interface AuthErrorMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.authError
  [k: string]: unknown
}

export interface RequestDeleteConnectionMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.requestDeleteConnection
  connectionId: string
  [k: string]: unknown
}

export interface BridgeReadyMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.bridgeReady
  protocolVersion: number
  [k: string]: unknown
}

/**
 * Posted when the page's graph canvas has first drawn: once React Flow has
 * measured every node, or straight away when there are no nodes to measure.
 * Native keeps its skeleton over the WebView until this arrives. Additive, so
 * BRIDGE_PROTOCOL_VERSION is unchanged: a native build that predates it has
 * no branch for the type and ignores it, and native reveals the canvas on its
 * own load timeout after the handshake when a page never sends it.
 */
export interface GraphRenderedMessage {
  type: typeof WEB_TO_NATIVE_MESSAGE_TYPES.graphRendered
  [k: string]: unknown
}

export type WebToNativeMessage =
  | ShowMoveInfoMessage
  | ConnectionCreatedMessage
  | ConnectionDeletedMessage
  | NodePositionUpdatedMessage
  | AuthErrorMessage
  | RequestDeleteConnectionMessage
  | BridgeReadyMessage
  | GraphRenderedMessage

/* Type guards. Each narrows from `{ [k: string]: unknown }` and checks the
 * type discriminator in the body — mirrors the existing mobile predicate
 * style and avoids polluting the message interfaces with an index signature. */

export function isShowMoveInfoMessage(
  p: { [k: string]: unknown },
): p is ShowMoveInfoMessage {
  return (
    p.type === WEB_TO_NATIVE_MESSAGE_TYPES.showMoveInfo &&
    typeof p.seriesMoveId === 'string' &&
    typeof p.moveId === 'string' &&
    typeof p.name === 'string' &&
    typeof p.description === 'string' &&
    (p.thumbnailUrl === undefined || p.thumbnailUrl === null || typeof p.thumbnailUrl === 'string') &&
    (p.url === undefined || p.url === null || typeof p.url === 'string')
  )
}

export function isRequestDeleteConnectionMessage(
  p: { [k: string]: unknown },
): p is RequestDeleteConnectionMessage {
  return (
    p.type === WEB_TO_NATIVE_MESSAGE_TYPES.requestDeleteConnection &&
    typeof p.connectionId === 'string'
  )
}

export function isBridgeReadyMessage(
  p: { [k: string]: unknown },
): p is BridgeReadyMessage {
  return (
    p.type === WEB_TO_NATIVE_MESSAGE_TYPES.bridgeReady &&
    typeof p.protocolVersion === 'number'
  )
}

/**
 * Native bridge methods exposed by the web side at `window.__wgBridge`.
 * Native injects JS that calls these via injectJavaScript.
 *
 * `setToken` delivers the member's access token over the private bridge
 * channel after the `bridgeReady` handshake — the token is never exposed as
 * a page-readable global.
 */
export const NATIVE_BRIDGE_METHODS = {
  setEditMode: 'setEditMode',
  invalidateSeries: 'invalidateSeries',
  confirmDeleteConnection: 'confirmDeleteConnection',
  setToken: 'setToken',
} as const

export type NativeBridgeMethod =
  (typeof NATIVE_BRIDGE_METHODS)[keyof typeof NATIVE_BRIDGE_METHODS]

export interface WgBridge {
  [NATIVE_BRIDGE_METHODS.setEditMode]: (enabled: boolean) => void
  [NATIVE_BRIDGE_METHODS.invalidateSeries]: () => void
  [NATIVE_BRIDGE_METHODS.confirmDeleteConnection]: (connectionId: string) => void
  [NATIVE_BRIDGE_METHODS.setToken]: (token: string) => void
}
